/**
 * Phase 16 (F12) — Copy Dishes from Existing Station.
 *
 * A station-form sidekick dialog that lets the user lift an entire dish/
 * ingredient list off any saved station (across Standard / Live / Crew Mess
 * Live / Theme Buffet child stations) and drop it into the station being
 * edited. Append or replace modes, with a search filter and a source-lib
 * dropdown.
 *
 * Snapshot semantics: the picker returns a fresh `Dish[]` copy. The form
 * the picker lives in is responsible for merging the result into its
 * local state; the source library doc is NEVER mutated by this flow.
 *
 * Props:
 *   open: bool — controlled visibility
 *   onOpenChange: fn(bool)
 *   currentDishes: Dish[] — what's already in the form (used to compute
 *                          dedup hint badge counts)
 *   onPick: fn(Dish[], { mode: "append"|"replace" })
 */
import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Loader2, ClipboardCopy, Search } from "lucide-react";
import { listStationSourceList } from "@/api/mealPlanApi";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

const SOURCE_TYPES = [
  { value: "all",       label: "All sources" },
  { value: "standard",  label: "Standard Stations" },
  { value: "live",      label: "Live Stations" },
  { value: "crew_live", label: "Crew Mess Live Stations" },
  { value: "theme",     label: "Theme Buffet Stations" },
];

const SOURCE_BADGE_COLOR = {
  standard_stations:        "bg-slate-100 text-slate-700",
  live_stations:            "bg-orange-100 text-orange-700",
  crew_mess_live_stations:  "bg-rose-100 text-rose-700",
  theme:                    "bg-emerald-100 text-emerald-700",
};
const SOURCE_BADGE_LABEL = {
  standard_stations:       "Standard",
  live_stations:           "Live",
  crew_mess_live_stations: "Crew Live",
  theme:                   "Theme",
};

export default function CopyDishesFromStationDialog({
  open, onOpenChange, currentDishes = [], onPick,
}) {
  const { toast } = useToast();
  const [type, setType] = useState("all");
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState("append");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickedId, setPickedId] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setPickedId(null);
    listStationSourceList(type)
      .then((data) => { if (!cancelled) setItems(data || []); })
      .catch((e) => {
        toast({
          title: "Couldn't load station list",
          description: String(e?.message || e),
          variant: "destructive",
        });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, type, toast]);

  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => (it.name || "").toLowerCase().includes(q));
  }, [items, search]);

  const handleCopy = () => {
    const chosen = items.find((x) => x.id === pickedId);
    if (!chosen) return;
    // Snapshot — clone dishes deeply so subsequent edits on the source
    // never bleed into the form state. Materials snapshotted too.
    const clone = (chosen.dishes || []).map((d) => ({
      name: d?.name || "",
      material_id: d?.material_id || null,
      material_name: d?.material_name || "",
    }));
    onPick(clone, { mode });
    toast({
      title: "Dishes copied",
      description:
        `${clone.length} dish${clone.length === 1 ? "" : "es"} copied from ` +
        `"${chosen.name}" (${mode}).`,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" lockOutsideClick>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCopy className="h-4 w-4 text-amber-600" />
            Copy dishes from an existing station
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Select value={type} onValueChange={setType}>
              <SelectTrigger
                className="h-8 w-[220px] text-xs"
                data-testid="copy-dishes-source-type"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SOURCE_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by station name…"
                className="h-8 pl-8 text-xs"
                data-testid="copy-dishes-search"
              />
            </div>
          </div>

          <div className="max-h-[320px] overflow-y-auto rounded border bg-slate-50">
            {loading ? (
              <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading stations…
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
                No stations match.
              </div>
            ) : (
              <ul>
                {filtered.map((it) => (
                  <li key={`${it.source_lib}::${it.id}`}>
                    <button
                      type="button"
                      onClick={() => setPickedId(it.id)}
                      className={cn(
                        "flex w-full items-center justify-between gap-2 border-b px-3 py-2 text-left text-xs hover:bg-amber-50",
                        pickedId === it.id && "bg-amber-100 hover:bg-amber-100",
                      )}
                      data-testid={`copy-dishes-row-${it.id}`}
                    >
                      <span className="flex-1 truncate">
                        <span className="font-medium">{it.name}</span>
                      </span>
                      <span className={cn(
                        "rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase",
                        SOURCE_BADGE_COLOR[it.source_lib] || "bg-slate-100 text-slate-700",
                      )}>
                        {SOURCE_BADGE_LABEL[it.source_lib] || it.source_lib}
                      </span>
                      <span className="rounded bg-white px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
                        {it.dish_count} dish{it.dish_count === 1 ? "" : "es"}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex items-center justify-between rounded border bg-white px-3 py-2 text-xs">
            <span className="font-medium">Mode</span>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="copy-dishes-mode"
                  checked={mode === "append"}
                  onChange={() => setMode("append")}
                  data-testid="copy-dishes-mode-append"
                />
                Append to current ({(currentDishes || []).length})
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="copy-dishes-mode"
                  checked={mode === "replace"}
                  onChange={() => setMode("replace")}
                  data-testid="copy-dishes-mode-replace"
                />
                Replace current
              </label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="copy-dishes-cancel"
          >Cancel</Button>
          <Button
            onClick={handleCopy}
            disabled={!pickedId}
            data-testid="copy-dishes-confirm"
          >Copy dishes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
